import toast from 'react-hot-toast';
import { isRequestError } from 'utils/requestError';
import { saveListExcel } from 'utils/tableExport/saveListExcel';
import type { ColumnDef } from '@tanstack/react-table';
import {
  Card,
  InputDateTime,
  InputSelect,
  InputText,
  TableListData,
} from 'components';
import {
  Button,
  InputWraper,
  useForm,
  NumberUtils,
  IUseFormStructureItem,
} from 'modules';
import React, { useCallback, useState } from 'react';
import {
  useGetPointTransactionsQuery,
  useGetPointTransactionsExportQuery,
} from 'services';
import type { GetListPointTransactionEntity } from 'services/openapi';

import {
  columnsDefined,
  convertSelectStatusOptions,
  convertSelectTransactionTypeOptions,
  convertVariables,
  type FilterType,
  getInitFilterValues,
} from './config';
import { useLocation } from 'react-router-dom';
import * as Yup from 'yup';

const PointTransactionsList = () => {
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const initFilterValues = getInitFilterValues(urlParams);
  console.log('annnnnnn', initFilterValues);
  const [variables, setVariables] = useState<FilterType>(initFilterValues);
  const [dataTable, setDataTable] = useState([]);

  const handleFilter = useCallback(
    (e: FilterType) => {
      setVariables({
        ...variables,
        ...e,
        // // refreshData: variables.refreshData + 1,
      });
    },
    [variables],
  );

  const filterOptions: IUseFormStructureItem[] = [
    {
      name: 'transactionCode',
      label: 'Mã giao dịch',
      defaultValue: initFilterValues.transactionCode,
    },
    {
      name: 'transactionStatus',
      label: 'Trạng thái',
      defaultValue: initFilterValues.transactionStatus,
      validate: Yup.string().required('Vui lòng chọn Trạng thái'),
    },
    {
      name: 'transactionType',
      label: 'Loại giao dịch',
      defaultValue: initFilterValues.transactionType,
    },
    {
      name: 'fromUserSearchString',
      label: 'Bên chuyển',
    },
    {
      name: 'toUserSearchString',
      label: 'Bên thụ hưởng',
    },
    {
      name: 'fromTime',
      label: 'Ngày & giờ bắt đầu',
      defaultValue: initFilterValues.fromTime,
      validate: Yup.string()
        .required('Vui lòng chọn Ngày & giờ bắt đầu')
        .test(
          'comp-fromdate-todate',
          'Ngày & giờ bắt đầu phải nhỏ hơn Ngày & giờ kết thúc',
          function (value) {
            if (value)
              return (
                new Date(value).getTime() <
                new Date(this.parent?.toTime).getTime()
              );
            return false;
          },
        )
        .test(
          'comp-fromdate-todate-60-days',
          'Khoảng cách Ngày & giờ bắt đầu và Ngày & giờ kết thúc không vượt quá 60 ngày',
          function (value) {
            if (value) {
              const dateEnd: Date = new Date(this.parent?.toTime);
              dateEnd.setHours(0, 0, 0, 0);
              dateEnd.setDate(dateEnd.getDate() - 60);
              const dateStart: Date = new Date(value);
              dateEnd.setHours(0, 0, 0, 0);
              return dateEnd.getTime() <= dateStart.getTime();
            }
            return false;
          },
        ),
    },
    {
      name: 'toTime',
      label: 'Ngày & giờ kết thúc',
      defaultValue: initFilterValues.toTime,
      validate: Yup.string()
        .required('Vui lòng chọn Ngày & giờ kết thúc')
        .test(
          'comp-fromdate-todate',
          'Ngày & giờ kết thúc phải lớn hơn Ngày & giờ bắt đầu',
          function (value) {
            if (value)
              return (
                new Date(this.parent?.fromTime).getTime() <
                new Date(value).getTime()
              );
            return false;
          },
        )
        .test(
          'comp-fromdate-todate-60-days',
          'Khoảng cách Ngày & giờ bắt đầu và Ngày & giờ kết thúc không vượt quá 60 ngày',
          function (value) {
            if (value) {
              const dateEnd: Date = new Date(value);
              dateEnd.setHours(0, 0, 0, 0);
              dateEnd.setDate(dateEnd.getDate() - 60);
              const dateStart: Date = new Date(this.parent?.fromTime);
              dateEnd.setHours(0, 0, 0, 0);
              return dateEnd.getTime() <= dateStart.getTime();
            }
            return false;
          },
        ),
    },
  ];

  const { getInputProps, handleSubmit } = useForm({
    structure: filterOptions,
    onSubmit: handleFilter,
  });

  const normalizedPhoneNumber = (phoneNumber?: string) => {
    return phoneNumber ? phoneNumber.replace(/\+84/, '0') : '';
  };

  const normalizeData = (data: GetListPointTransactionEntity) => {
    const dataSummary: any = data?.summary?.find(
      (summary: any) => summary.title === 'All',
    );
    return dataSummary.total;
  };

  const { isFetching: isLoadingExport, refetch } =
    useGetPointTransactionsExportQuery({
      ...convertVariables(variables),
      page: '1',
      pageSize: String(
        (dataTable as unknown as GetListPointTransactionEntity)?.total,
      ),
    });

  const handleExportExcel = async () => {
    if (isLoadingExport) return;
    try {
      const allData = await refetch({ throwOnError: true });
      const summary = allData.data as any;
      await saveListExcel(
        allData.data?.data,
        columnsDefined as ColumnDef<GetListPointTransactionEntity>[],
        'Danh_sach_cong_tru_diem',
        true,
        {
          totals: {
            beneficiary: 'TỔNG',
            amount: NumberUtils.toFormatNumber(Number(normalizeData(summary))),
          },
        },
      );
    } catch (error) {
      if (!isRequestError(error))
        toast.error(
          error instanceof Error ? error.message : 'Không thể tải danh sách',
        );
    }
  };

  return (
    <>
      <Card className='container-fluid mb20'>
        <form onSubmit={handleSubmit}>
          <div className='row'>
            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('transactionCode')}
                renderInput={InputText}
              />
            </div>

            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('transactionType')}
                renderInput={(props) => (
                  <InputSelect
                    {...props}
                    options={convertSelectTransactionTypeOptions()}
                  />
                )}
              />
            </div>

            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('fromUserSearchString')}
                renderInput={InputText}
              />
            </div>

            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('toUserSearchString')}
                renderInput={InputText}
              />
            </div>

            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('transactionStatus')}
                isRequired
                renderInput={(props) => (
                  <InputSelect
                    {...props}
                    options={convertSelectStatusOptions()}
                  />
                )}
              />
            </div>

            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('fromTime')}
                isRequired
                renderInput={InputDateTime}
              />
            </div>

            <div className='col-lg-3 col-12'>
              <InputWraper
                inputProps={getInputProps('toTime')}
                isRequired
                renderInput={InputDateTime}
              />
            </div>
          </div>
          <div className='textCenter mt16 wrapButtonFilter'>
            <Button label={'Tìm kiếm'} type='submit' buttonType='success' />
            <Button
              className='ml15'
              onClick={handleExportExcel}
              label={'Tải về DS'}
              type='button'
              buttonType='info'
              isLoading={isLoadingExport}
            />
          </div>
        </form>
      </Card>

      <TableListData<GetListPointTransactionEntity>
        columns={columnsDefined as ColumnDef<GetListPointTransactionEntity>[]}
        useQuery={useGetPointTransactionsQuery}
        variables={variables}
        setVariables={setVariables}
        convertVariables={convertVariables}
        filterKey='searchDetailContent'
        orderColumn
        callBackData={setDataTable}
        customTopRow={(data) => {
          if (data.summary) {
            return (
              <>
                <tr>
                  <td colSpan={6}></td>
                  <td>
                    <strong>TỔNG</strong>
                  </td>
                  <td>
                    <strong>
                      {NumberUtils.toFormatNumber(Number(normalizeData(data)))}
                    </strong>
                  </td>
                  <td></td>
                  <td></td>
                </tr>
              </>
            );
          }
        }}
      />
    </>
  );
};

export default PointTransactionsList;
